import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { forwardRef, useImperativeHandle } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { get, post } from "../../services/api";
import { toast } from "react-toastify";
import "./RegistrationForm.css";
import { useFormContext } from "./RegistrationFormContext";
import TagModal from "../../components/TagModal";
import NotificationEventTypes from "../../constants/notificationEventsEnum";
import StepSuccessModal from "./StepSuccessModal";

const Step3Attachments = forwardRef(({ isOpen, moveToNextStep }, ref) => {
  const roleId = localStorage.getItem("roleId");
  const token = localStorage.getItem("token");
  const { sharedData } = useFormContext();
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const {
    register,
    watch,
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {},
  });

  const [pupils, setPupils] = useState([]);
  const [selectedPupilId, setSelectedPupilId] = useState(null);
  const [pupilError, setPupilError] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state

  const [dataSubmitting, setDataSubmitting] = useState(false);
  const [parentSubmit, setParentSubmit] = useState(false);
  const [tagModalData, setTagModalData] = useState({
    isOpen: false,
    field: null,
    index: null,
    tagName: "",
    tagComments: "",
    tagPriority: null,
  });

  useEffect(() => {
    if (isOpen && sharedData.userId) {
      fetchPupils();
    }
  }, [isOpen, sharedData.userId]);

  const fetchPupils = async () => {
    setIsLoading(true);
    try {
      console.log("Fetching pupils with userId:", sharedData.userId); // Debug
      const response = await get(
        `Student/GetPupilsInformationByUserId?userId=${sharedData.userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      console.log("Fetched pupils:", response); // Debug
      setPupils(response || []);

      if (response && response.length > 0) {
        const firstPupilId = response[response.length - 1].id;
        setSelectedPupilId(firstPupilId);
        handlePupilSelection({ target: { value: firstPupilId } });
      } else {
        setSelectedPupilId(null);
        setParentSubmit(false);
      }
    } catch (error) {
      console.error("Error fetching pupils:", error);
      setPupils([]);
      setSelectedPupilId(null);
    } finally {
      setIsLoading(false);
    }
  };

  const [uploadedFiles, setUploadedFiles] = useState({
    pupilPhoto: [],
    birthCertificate: [],
    residencyIdNonSaudis: [],
    fatherResidencyIdNonSaudis: [],
    familyIdCardSaudis: [],
    fatherIdCardSaudis: [],
    studentPassport: [],
    fatherPassport: [],
    motherPassport: [],
    vaccinationCertificate: [],
    previousSchoolReports: [],
    equivalenceCertificate: [],
    schoolLeavingCertificate: [],
  });

  const fileInputRefs = useRef({});

  const handleTagClick = (field, index) => {
    const file = uploadedFiles[field][index];
    let documentType = "";
    const fileName = file.fileName || "";
    const matches = fileName.match(/-([^-]+)-/);
    if (matches && matches[1]) {
      documentType = matches[1];
    } else {
      documentType = fileName;
    }
    setTagModalData({
      isOpen: true,
      field,
      index,
      tagName: file.tagName || documentType,
      tagComments: file.tagComments || "Kindly Reload The " + documentType,
      tagPriority: file.tagPriority || null,
      documentId: file.id || 0,
      fileName: file.fileName || "",
    });
  };

  const handleTagSave = (data) => {
    setUploadedFiles((prev) => ({
      ...prev,
      [tagModalData.field]: prev[tagModalData.field].map((file, i) =>
        i === tagModalData.index
          ? {
              ...file,
              tagName: data.tagName,
              tagComments: data.tagComments,
              tagPriority: data.tagPriority,
            }
          : file
      ),
    }));

    setTagModalData({
      isOpen: false,
      field: null,
      index: null,
      tagName: "",
      tagComments: "",
      tagPriority: null,
      documentId: 0,
    });
  };

  const handleTagClose = () => {
    setTagModalData({
      isOpen: false,
      field: null,
      index: null,
      tagName: "",
      tagComments: "",
      tagPriority: null,
      documentId: 0,
    });
  };

  const handleFileChange = (field) => async (event) => {
    if (!selectedPupilId) {
      setPupilError(true);
      toast.error("Please select a pupil.");
      return;
    }

    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (field === "pupilPhoto") {
      const file = files[0];
      const reader = new FileReader();

      reader.onloadend = () => {
        const updatedFile = {
          id: uploadedFiles[field][0]?.id || 0,
          createdBy: localStorage.getItem("userId"),
          updatedBy: localStorage.getItem("userId"),
          fileName: file.name,
          filePath: "",
          base64String: reader.result.split(",")[1],
          tagName: uploadedFiles[field][0]?.tagName || "",
          tagComments: uploadedFiles[field][0]?.tagComments || "",
          tagPriority: uploadedFiles[field][0]?.tagPriority || null,
        };

        setUploadedFiles((prev) => ({
          ...prev,
          [field]: [updatedFile],
        }));
      };

      reader.readAsDataURL(file);
    } else {
      const isParentRole = ["9", "10", "11"].includes(roleId);
      const taggedFileIndices = uploadedFiles[field]
        .map((file, index) => (file.tagName ? index : -1))
        .filter((index) => index !== -1);

      if (isParentRole && taggedFileIndices.length === 0 && parentSubmit) {
        toast.error("You can only replace tagged documents.");
        return;
      }

      if (
        isParentRole &&
        files.length > taggedFileIndices.length &&
        parentSubmit
      ) {
        toast.error(
          `You can only upload up to ${taggedFileIndices.length} file(s) to replace tagged documents.`
        );
        return;
      }

      const filePromises = files.map((file, fileIndex) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const taggedIndex = taggedFileIndices[fileIndex];
            const isReplacingTagged = isParentRole && taggedIndex !== undefined;

            resolve({
              id: isReplacingTagged ? uploadedFiles[field][taggedIndex].id : 0,
              createdBy: localStorage.getItem("userId"),
              updatedBy: localStorage.getItem("userId"),
              fileName: file.name,
              filePath: "",
              base64String: reader.result.split(",")[1],
              tagName: isReplacingTagged
                ? uploadedFiles[field][taggedIndex].tagName
                : "",
              tagComments: isReplacingTagged
                ? uploadedFiles[field][taggedIndex].tagComments
                : "",
              tagPriority: isReplacingTagged
                ? uploadedFiles[field][taggedIndex].tagPriority
                : null,
            });
          };
          reader.readAsDataURL(file);
        });
      });

      const fileDataArray = await Promise.all(filePromises);

      setUploadedFiles((prev) => {
        if (isParentRole && taggedFileIndices.length > 0) {
          const newFiles = [...prev[field]];
          fileDataArray.forEach((newFile, fileIndex) => {
            const taggedIndex = taggedFileIndices[fileIndex];
            if (taggedIndex !== undefined) {
              newFiles[taggedIndex] = newFile;
            }
          });
          return { ...prev, [field]: newFiles };
        }
        return {
          ...prev,
          [field]: [...prev[field], ...fileDataArray],
        };
      });
    }

    if (fileInputRefs.current[field]) {
      fileInputRefs.current[field].value = "";
    }
  };

  const handlePreviewFile = (file) => {
    const isPDF = file.fileName.toLowerCase().endsWith(".pdf");

    if (file.base64String) {
      const byteCharacters = atob(file.base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: isPDF ? "application/pdf" : "image/jpeg",
      });

      const fileUrl = URL.createObjectURL(blob);
      window.open(fileUrl, "_blank");

      setTimeout(() => URL.revokeObjectURL(fileUrl), 5000);
    } else if (file.filePath) {
      const attachmentUrl =
        process.env.REACT_APP_LIVE_DOMAIN_URL +
        "/ExternalImages/" +
        file.filePath;
      window.open(attachmentUrl, "_blank");
    }
  };

  const handleRemoveFile = (field, index = null) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to remove this document?"
    );

    if (confirmDelete) {
      if (index === null) {
        setUploadedFiles((prev) => ({ ...prev, [field]: [] }));
      } else {
        const newFiles = uploadedFiles[field].filter((_, i) => i !== index);
        setUploadedFiles((prev) => ({ ...prev, [field]: newFiles }));
      }
    }
  };

  useImperativeHandle(ref, () => ({
    save: onSaveForm,
  }));

  const onSaveForm = async () => {
    if (!token) {
      console.error("Unauthorized.");
      return;
    }

    if (!selectedPupilId) {
      setPupilError(true);
      return;
    } else {
      setPupilError(false);
    }

    setDataSubmitting(true);

    const formatFileNameIfNew = (field, file, index) => {
      if (file.id !== 0) {
        return file.fileName;
      }
      return field === "pupilPhoto"
        ? formatFileName(field, file.fileName, 1).replace(/ - \d+\./, ".")
        : formatFileName(field, file.fileName, index + 1);
    };

    const pupilPhotoDocument =
      uploadedFiles.pupilPhoto.length > 0
        ? [
            {
              id: uploadedFiles.pupilPhoto[0].id || 0,
              createdBy: localStorage.getItem("userId"),
              updatedBy: localStorage.getItem("userId"),
              fileName: formatFileNameIfNew(
                "pupilPhoto",
                uploadedFiles.pupilPhoto[0],
                1
              ),
              documentType: "pupilPhoto",
              base64String: uploadedFiles.pupilPhoto[0].base64String || "",
              filePath: uploadedFiles.pupilPhoto[0].filePath || "",
            },
          ]
        : [];

    const documentFields = Object.keys(uploadedFiles).filter(
      (field) => field !== "pupilPhoto"
    );

    const studentDocuments = [
      ...pupilPhotoDocument,
      ...documentFields.flatMap((field) => {
        return uploadedFiles[field].map((file, index) => ({
          id: file.id || 0,
          createdBy: localStorage.getItem("userId"),
          updatedBy: localStorage.getItem("userId"),
          fileName: formatFileNameIfNew(field, file, index),
          documentType: field,
          base64String: file.base64String || "",
          filePath: file.filePath || "",
        }));
      }),
    ];

    const hasNewFile = studentDocuments.some((doc) => doc.id === 0);

    const formData = {
      studentFirstRegistrationId: selectedPupilId,
      studentDocuments,
      FormId: 75,
      ...(hasNewFile && {
        NotificationEventTypeIds: NotificationEventTypes.UPLOAD.toString(),
      }),
    };

    try {
      const response = await post(
        "Student/UpdateAdmissionInqueryAttachments",
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (response.isSuccess) {
        setShowSuccessModal(true);
        return true;
      } else {
        toast.error(response.message || "An unexpected error occurred");
        return false;
      }
    } catch (error) {
      console.error("Error.", error);
      toast.error("Failed to save documents.");
      return false;
    } finally {
      setDataSubmitting(false);
    }
  };

  const handlePupilSelection = async (event) => {
    const pupilId = event.target.value;
    setSelectedPupilId(pupilId);
    setPupilError(false);
    if (!pupilId) {
      setPupilError(true);
      return;
    }

    try {
      const response = await get(
        `Student/GetAdmissionInqueryAttachments?studentFirstRegistrationId=${pupilId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (response) {
        const updatedFiles = Object.keys(uploadedFiles).reduce((acc, key) => {
          acc[key] = [];
          return acc;
        }, {});

        response.studentDocuments.forEach((document) => {
          const docType = document.documentType;
          if (updatedFiles.hasOwnProperty(docType)) {
            updatedFiles[docType].push({
              id: document.id,
              fileName: document.fileName,
              filePath: document.filePath,
              base64String: document.base64String || "",
              createdBy: document.createdBy,
              updatedBy: document.updatedBy,
              tagName: document.tagName || "",
              tagComments: document.tagComments || "",
              tagPriority: document.tagPriority || null,
            });
          }
        });

        setUploadedFiles(updatedFiles);
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
    }

    if (pupils.length > 0) {
      const selectedPupil = pupils.find(
        (pupil) => pupil.id === Number(pupilId)
      );
      setParentSubmit(selectedPupil?.isSubmit || false);
    }
  };

  const getFileExtension = (fileName) => {
    return fileName?.split(".").pop();
  };

  const formatFileName = (field, fileName, count) => {
    const documentTypeName = field
      .replace(/([A-Z])/g, " $1")
      .toLowerCase()
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());

    const fileExtension = getFileExtension(fileName);

    if (field === "pupilPhoto") {
      return `${documentTypeName}.${fileExtension}`;
    }

    return `${documentTypeName}-${count}.${fileExtension}`;
  };

  const handleModalAddAnother = () => {
    setShowSuccessModal(false);
  };

  const handleModalNavigateToPrevSchools = () => {
    setShowSuccessModal(false);
    moveToNextStep(4);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="form-step">
      <div className="row g-3">
        <div className="col-12">
          <div className="mb-3" style={{ width: "300px" }}>
            <label className="form-label form-label-new">
              Select Pupil <span className="text-danger fs-9">*</span>
            </label>
            <select
              className={`form-select`}
              onChange={handlePupilSelection}
              value={selectedPupilId || ""}
              disabled={isLoading}
            >
              <option value="">Select</option>
              {pupils.map((pupil) => (
                <option key={pupil.id} value={pupil.id}>
                  {pupil.firstName + " " + pupil.lastName}
                </option>
              ))}
            </select>
            {pupilError && (
              <small className="text-danger">This field is required.</small>
            )}
          </div>

          {selectedPupilId && (
            <>
              <h5 className="mb-3 p-2 px-3 text-white bg-brand rounded-pill">
                Documents
              </h5>

              <div className="row g-3">
                {Object.keys(uploadedFiles).map((field) => (
                  <div className="col-md-3" key={field}>
                    <div className="border border-black-50 p-3 position-relative h-100">
                      <label className="form-label text-center">
                        {field.replace(/([A-Z])/g, " $1")}
                        <i className="fa fa-paperclip fs-8 ms-1"></i>
                      </label>
                      <div className="attachment-wrapper">
                        <div className="attachment-placeholder">
                          <input
                            type="file"
                            accept="image/*,application/pdf"
                            multiple={field !== "pupilPhoto"}
                            className="form-control mt-2"
                            onChange={handleFileChange(field)}
                            ref={(el) => (fileInputRefs.current[field] = el)}
                            disabled={
                              roleId === "3" || roleId === "2"
                                ? false
                                : !["9", "10", "11"].includes(roleId)
                                ? true
                                : parentSubmit &&
                                  !uploadedFiles[field].some(
                                    (file) => file.tagName
                                  )
                            }
                          />
                          <div className="text-center fs-10 mt-2">
                            {field == "pupilPhoto"
                              ? "Please upload one photo (JPG/PNG)."
                              : "You can upload multiple certificates (PDF, JPG, PNG)."}
                          </div>
                        </div>

                        <div className="attachment-preview mt-2">
                          {uploadedFiles[field].length > 0
                            ? uploadedFiles[field].map((file, index) => {
                                const isFromAPI = file.id !== 0;
                                const isParentRole = ["9", "10", "11"].includes(
                                  roleId
                                );

                                return (
                                  <div
                                    key={index}
                                    className="file-item mb-1 d-flex justify-content-between align-items-center position-relative"
                                  >
                                    <div className="att-file-name">
                                      {isFromAPI
                                        ? file.fileName
                                        : formatFileName(
                                            field,
                                            file.fileName,
                                            index + 1
                                          )}
                                    </div>

                                    <div className="d-flex gap-1">
                                      <span
                                        className="attachment-action-btn preview-btn"
                                        onClick={() => handlePreviewFile(file)}
                                        data-tooltip-id="global-tooltip"
                                        data-tooltip-content="Preview"
                                      >
                                        <i className="fa fa-eye"></i>
                                      </span>
                                      {isFromAPI &&
                                        (!isParentRole || file.tagName ? (
                                          <span
                                            className="attachment-action-btn tag-btn"
                                            onClick={() =>
                                              !isParentRole || file.tagName
                                                ? handleTagClick(field, index)
                                                : null
                                            }
                                            data-tooltip-id="global-tooltip"
                                            data-tooltip-content="Tag"
                                          >
                                            {file.tagName ? (
                                              <span className="badge bg-primary">
                                                {file.tagName}
                                              </span>
                                            ) : (
                                              !isParentRole && (
                                                <i className="fa fa-tag"></i>
                                              )
                                            )}
                                          </span>
                                        ) : null)}
                                      {/* <span
                                      className="attachment-action-btn remove-btn"
                                      onClick={() => {
                                        if (roleId !== "3" && parentSubmit) {
                                          return;
                                        }
                                        handleRemoveFile(field, index);
                                      }}
                                      data-tooltip-id="global-tooltip"
                                      data-tooltip-content={
                                        roleId !== "3" && parentSubmit
                                          ? "Cannot remove approved document"
                                          : "Remove"
                                      }
                                      style={{
                                        cursor:
                                          roleId !== "3" && parentSubmit
                                            ? "not-allowed"
                                            : "pointer",
                                        opacity:
                                          roleId !== "3" && parentSubmit
                                            ? 0.5
                                            : 1,
                                      }}
                                    >
                                      <i className="fa fa-remove"></i>
                                    </span> */}
                                    </div>
                                  </div>
                                );
                              })
                            : null}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="mt-4">
          <button
            type="button"
            className="btn btn-sm btn-primary"
            onClick={onSaveForm}
            disabled={dataSubmitting}
          >
            {dataSubmitting ? (
              <span
                className="spinner-border spinner-custom-size me-1"
                aria-hidden="true"
              ></span>
            ) : null}
            Save Documents
          </button>
        </div>
      </div>

      {tagModalData.isOpen &&
        createPortal(
          <TagModal
            tagName={tagModalData.tagName}
            tagComments={tagModalData.tagComments}
            tagPriority={tagModalData.tagPriority}
            onSave={handleTagSave}
            onClose={handleTagClose}
            documentId={tagModalData.documentId}
            studentFirstRegistrationId={selectedPupilId}
            fileName={tagModalData.fileName}
          />,
          document.body
        )}

      <StepSuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        onAddAnother={handleModalAddAnother}
        onNavigate={handleModalNavigateToPrevSchools}
        message="Documents Saved Successfully!"
        nextButtonText="Go to Previous Schools"
      />
    </div>
  );
});

export default Step3Attachments;
